import LoadingIconComponent from '@components/icons/loadingIcon';
import MaxSubscriptionIcon from '@components/icons/maxSubscriptionIcon';
import {
  getActiveSectionDetailsUrl,
  getRedirectFallbackToActiveSectionUrl,
  getRegisterPageLink,
} from '@utils/link-utils-web';
import { useRouter } from 'next/router';
import PathCheckmarkIconComponent from '../../icons/PathCheckmarkIcon';
import {
  Heading3,
  Paragraph2,
  Heading2,
  Heading1,
  Button1Text,
  Paragraph1,
  TextLink,
  Paragraph3,
} from '../texts/Texts';
import Link from 'next/link';
import { PricingPageSubscriptionInterval } from '@pages/pro';
import SimpleArrowDownIconComponent from '@components/icons/simpleArrowDownIcon';
import { useState } from 'react';
import { BodyText, Heading5 } from '@components/web-landingpage/Texts';
import CheckmarkIconComponent from '@components/icons/checkmarkIcon';
import { PlanComparisonState } from './Pricing.Page';
import {
  getSpecialDiscountOfferCouponForCheckout,
  SpecialOfferWindow,
} from '@utils/special-offer-time-utils';
import MascotSmilingComponent from '@components/illustrations/MascotSmiling';
import { medium_base } from '../texts/Styles';
import { twMerge } from 'tailwind-merge';
import OutlineButtonLinkComponent from '@components/buttons/OutlineButtonLink';

export function PlanComparisonComponent({
  planComparisonState,
  interval,
}: {
  planComparisonState: PlanComparisonState;
  interval: PricingPageSubscriptionInterval;
}) {
  if (planComparisonState.type === 'error') {
    return (
      <Heading3 className="text-center text-marketing-form-error">
        Something went wrong. Please reload the page.
      </Heading3>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 md:flex-row">
      <div className="relative flex flex-1 basis-[0]">
        <MascotSmilingComponent className="absolute top-5 hidden h-[176px] w-[125px] -translate-y-full -scale-x-[1] transform md:block"></MascotSmilingComponent>
        <FreePlanCard></FreePlanCard>
      </div>
      <div className="flex flex-1 basis-[0]">
        <ProPlanCard
          planComparisonState={planComparisonState}
          interval={interval}
        ></ProPlanCard>
      </div>
      <div className="flex-1 basis-[0]">
        <MaxPlanCard
          planComparisonState={planComparisonState}
          interval={interval}
        ></MaxPlanCard>
      </div>
    </div>
  );
}

function FreePlanCard() {
  return (
    <div className="flex w-full flex-col rounded-3xl border-2 border-product2-border-secondary bg-product2-background-primary p-6 pb-8 md:p-4 md:pb-6 lg:p-6 lg:pb-8">
      <Heading2 className="leading-10 text-product2-content-primary">
        Basic
      </Heading2>
      <Paragraph2 className="mt-2 text-product2-content-secondary">
        For casual learners or those wanting a taste of coding
      </Paragraph2>
      <div className="mt-6 flex flex-col">
        <Heading3 className="font-aeonikFono text-[32px] font-bold leading-[44px]">
          Free
        </Heading3>
        <Paragraph3>Forever</Paragraph3>
      </div>
      <OutlineButtonLinkComponent
        href={getRegisterPageLink()}
        className="mt-6 flex h-14 w-full items-center justify-center rounded-2xl border-2 border-marketing-button-primary-text  
          text-marketing-button-primary-text
          hover:border-marketing-button-secondary-hover
          hover:text-marketing-button-secondary-hover"
      >
        <Button1Text>Start learning</Button1Text>
      </OutlineButtonLinkComponent>
      <ul className="mt-6 flex flex-col space-y-4 ">
        {['Learn for free on mobile'].map((perk, index) => (
          <li
            key={index}
            className={`flex items-center space-x-4 ${
              index < 2
                ? 'text-product2-content-primary'
                : 'text-product2-content-disabled'
            }`}
          >
            {index < 2 ? (
              <PathCheckmarkIconComponent className="h-6 w-6"></PathCheckmarkIconComponent>
            ) : (
              <div className="mx-1 h-[2px] w-4 bg-product2-content-disabled"></div>
            )}
            <Paragraph1>{perk}</Paragraph1>
          </li>
        ))}
      </ul>
      <ShowAllFeaturesSection
        mobileAppFeatures={[
          'Limited Access to Courses',
          'Limited Access to Career Paths',
          '2 Free Playgrounds',
        ]}
        webPlatformFeatures={[
          'Intro Section of All Courses',
          'Intro Section of All Career Paths',
          'Limited Access to AI Tutor',
          '2 Free Playgrounds',
        ]}
      ></ShowAllFeaturesSection>
    </div>
  );
}

function ProPlanCard({
  planComparisonState,
  interval,
}: {
  planComparisonState: PlanComparisonState;
  interval: 'monthly' | 'annually';
}) {
  const router = useRouter();
  const { couponId } = router.query;

  return (
    <div className="flex w-full flex-col rounded-3xl border-4 border-transparent bg-product2-background-secondary p-6 pb-8 md:p-4 md:pb-6 lg:p-6 lg:pb-8">
      <Heading2 className="leading-10 text-product2-content-primary">
        Pro
      </Heading2>
      <Paragraph2 className="mt-2 text-product2-content-secondary">
        For regular learners who prefer to learn on the go
      </Paragraph2>
      {planComparisonState.type === 'error' ? (
        <Paragraph1 className="mt-6 min-h-[66px] text-center text-product2-content-primary">
          Something went wrong. Please try again.
        </Paragraph1>
      ) : planComparisonState.type === 'loading' ? (
        <div
          className={`mt-6 flex h-[66px] items-center justify-center text-product2-background-accentLight`}
        >
          <LoadingIconComponent></LoadingIconComponent>
        </div>
      ) : (
        <div className="mt-6 flex flex-col">
          <Heading3 className="font-aeonikFono text-[32px] font-bold leading-[44px] text-product2-content-primary">
            {interval === 'monthly'
              ? planComparisonState.data.stripePricesPro.monthlyPrice
              : planComparisonState.data.stripePricesPro.yearlyPricePerMonth}
          </Heading3>
          <Paragraph3 className="text-product2-content-primary">
            /month {interval === 'annually' && 'billed yearly'}
          </Paragraph3>
        </div>
      )}
      <Link
        href={{
          pathname: '/go-to-checkout',
          query: {
            interval,
            ...(couponId ? { couponId } : {}),
            cancelUrl:
              getActiveSectionDetailsUrl() ||
              getRedirectFallbackToActiveSectionUrl(),
            upgradeType: 'pro',
          },
        }}
        className="mt-6 flex flex-shrink-0 justify-center rounded-2xl bg-product-button-primary-enabled-dark px-6 py-4 text-product2-content-primary shadow-sm hover:bg-product-button-primary-hovered-dark focus:outline-none md:px-4"
      >
        <Button1Text>
          {interval === 'annually'
            ? 'Start 7-day free trial'
            : 'Start with Pro'}
        </Button1Text>
      </Link>
      <ul className="mt-6 flex flex-col space-y-4 ">
        <li className="flex items-center space-x-4 text-product2-content-primary">
          <Paragraph1>Everything in Basic, plus:</Paragraph1>
        </li>
        {['Certificates', 'Unlimited Hearts'].map((perk, index) => (
          <li
            key={index}
            className="flex items-center space-x-4 text-product2-content-primary"
          >
            <PathCheckmarkIconComponent className="h-6 w-6"></PathCheckmarkIconComponent>
            <Paragraph1>{perk}</Paragraph1>
          </li>
        ))}
      </ul>
      <ShowAllFeaturesSection
        mobileAppFeatures={[
          'All Courses',
          'All Career Paths',
          'Unlimited Hearts',
          'No Ads',
          'Monthly Streak Repair',
          'Certificate',
          'Unlimited Playgrounds',
        ]}
        webPlatformFeatures={[
          'Intro Section of All Courses',
          'Intro Section of All Career Paths',
          'Limited Access to AI Tutor',
          'Unlimited Playgrounds',
        ]}
      ></ShowAllFeaturesSection>
    </div>
  );
}

function MaxPlanCard({
  planComparisonState,
  interval,
}: {
  planComparisonState: PlanComparisonState;
  interval: 'monthly' | 'annually';
}) {
  const router = useRouter();
  const { couponId } = router.query;

  // FIXME: to prevent discrepancies, we are removing this display of the recurring discount from the pricing page for the ab test period
  // const specialOfferWindow = isUserInSpecialDiscountOfferWindow();
  const specialOfferWindow: any = undefined;

  return (
    <>
      <div className="relative flex h-full w-full flex-col rounded-3xl border-4 border-product2-border-accent bg-product2-background-secondary bg-gradient-to-br from-[#6338B466] to-[#191B33] to-[70%] p-6 pb-8 md:p-4 md:pb-6 lg:p-6 lg:pb-8">
        <PercentOffBadge
          specialOfferWindow={specialOfferWindow}
        ></PercentOffBadge>
        <MaxSubscriptionIcon className="h-10 w-[70px]"></MaxSubscriptionIcon>
        <Paragraph2 className="mt-2 text-product2-content-secondary">
          For aspiring developers and those who take coding seriously
        </Paragraph2>
        {planComparisonState.type === 'error' ? (
          <Paragraph1 className="mt-6 min-h-[44px] text-center text-product2-content-primary">
            Something went wrong. Please try again.
          </Paragraph1>
        ) : planComparisonState.type === 'loading' ? (
          <div className="mt-6 flex h-[66px] items-center justify-center text-product2-background-accentLight">
            <LoadingIconComponent></LoadingIconComponent>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-baseline text-product2-content-primary">
              <div className="flex items-baseline space-x-4">
                <Heading3 className="font-aeonikFono text-[32px] font-bold leading-[44px]">
                  {!specialOfferWindow
                    ? interval === 'monthly'
                      ? planComparisonState.data.stripePricesMax.monthlyPrice
                      : planComparisonState.data.stripePricesMax
                          .yearlyPricePerMonth
                    : interval === 'monthly'
                      ? planComparisonState.data.stripePricesMax
                          .monthlyPriceWithDiscount
                      : planComparisonState.data.stripePricesMax
                          .yearlyPriceWithDiscountPerMonth}
                </Heading3>
                {specialOfferWindow && (
                  <StrikeThroughPrice
                    monthlyPrice={
                      planComparisonState.data.stripePricesMax.monthlyPrice
                    }
                    yearlyPricePerMonth={
                      planComparisonState.data.stripePricesMax
                        .yearlyPricePerMonth
                    }
                    interval={interval}
                  ></StrikeThroughPrice>
                )}
              </div>
            </div>
            <Paragraph3 className="text-product2-content-primary">
              /month {interval === 'annually' && 'billed yearly'}
            </Paragraph3>
          </>
        )}
        <Link
          href={{
            pathname: '/go-to-checkout',
            query: {
              interval,
              ...((couponId
                ? { couponId }
                : {
                    ...(specialOfferWindow
                      ? {
                          couponId: getSpecialDiscountOfferCouponForCheckout(),
                        }
                      : {}),
                  }) satisfies { couponId?: string | string[] }),
              cancelUrl:
                getActiveSectionDetailsUrl() ||
                getRedirectFallbackToActiveSectionUrl(),
              upgradeType: 'max',
            },
          }}
          className="mt-6 flex flex-shrink-0 justify-center rounded-2xl  bg-product-button-primary-enabled-dark px-6 py-4 text-product2-content-primary shadow-sm hover:bg-product-button-primary-hovered-dark focus:outline-none md:px-4"
        >
          <Button1Text>
            {interval === 'annually'
              ? 'Start 7-day free trial'
              : 'Start with Max'}
          </Button1Text>
        </Link>
        <ul className="mt-6 flex flex-col space-y-4 ">
          <li className="flex items-center space-x-4 text-product2-content-primary">
            <Paragraph1>Everything in Pro, plus:</Paragraph1>
          </li>
          {['Full Web access', 'AI Coding tutor'].map((perk, index) => (
            <li
              key={index}
              className="flex items-center space-x-4 text-product2-content-primary"
            >
              <PathCheckmarkIconComponent className="h-6 w-6"></PathCheckmarkIconComponent>
              <Paragraph1>{perk}</Paragraph1>
            </li>
          ))}
        </ul>
        <ShowAllFeaturesSection
          mobileAppFeatures={[
            'All Courses',
            'All Career Paths',
            'Unlimited Hearts',
            'No Ads',
            'Monthly Streak Repair',
            'Certificate',
            'Unlimited Playgrounds',
          ]}
          webPlatformFeatures={[
            'All Courses',
            'All Career Paths',
            'AI Coding Tutor',
            'Unlimited Playgrounds',
          ]}
        ></ShowAllFeaturesSection>
      </div>
    </>
  );
}

export function StrikeThroughPrice({
  monthlyPrice,
  yearlyPricePerMonth,
  interval,
  className,
}: {
  monthlyPrice: string;
  yearlyPricePerMonth: string;
  interval: 'monthly' | 'annually';
  className?: string;
}) {
  return (
    <Heading3
      className={twMerge(
        `text-product2-content-secondary line-through`,
        className,
      )}
    >
      {interval === 'monthly' ? monthlyPrice : yearlyPricePerMonth}
    </Heading3>
  );
}

export function PercentOffBadge({
  specialOfferWindow,
}: {
  specialOfferWindow?: SpecialOfferWindow;
}) {
  if (!specialOfferWindow) {
    return null;
  }

  return (
    <div
      className={`${medium_base} absolute right-6 top-6 flex h-6 items-center rounded-full bg-product2-accent-success px-2 text-product2-content-inverse`}
    >
      {specialOfferWindow.discountPercent}% OFF
    </div>
  );
}

function ShowAllFeaturesSection({
  mobileAppFeatures,
  webPlatformFeatures,
}: {
  mobileAppFeatures: string[];
  webPlatformFeatures: string[];
}) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  return (
    <>
      <button
        className="mt-6 flex w-fit items-center justify-center space-x-2 self-center md:hidden"
        onClick={() => setShowAllFeatures(!showAllFeatures)}
      >
        <SimpleArrowDownIconComponent
          className={`h-6 w-6 transition-transform duration-200  ${
            showAllFeatures ? 'rotate-180' : ''
          }`}
        ></SimpleArrowDownIconComponent>
        <TextLink className="whitespace-pre">See all features</TextLink>
      </button>
      <div
        className={`grid-rows grid transition-[grid-template-rows] duration-200 md:hidden ${
          showAllFeatures ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <ul className="mt-6 space-y-4">
            <li>
              <Heading5>Mobile App</Heading5>
            </li>
            {mobileAppFeatures.map((perk, index) => (
              <li key={index} className="flex items-center space-x-4">
                <CheckmarkIconComponent className="h-6 w-6"></CheckmarkIconComponent>
                <BodyText>{perk}</BodyText>
              </li>
            ))}
          </ul>
          <ul className="mt-4 space-y-4">
            <li>
              <Heading5>Web Platform</Heading5>
            </li>
            {webPlatformFeatures.map((perk, index) => (
              <li key={index} className="flex items-center space-x-4">
                <CheckmarkIconComponent className="h-6 w-6"></CheckmarkIconComponent>
                <BodyText>{perk}</BodyText>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
